/**
 * ImmobileFields — F-003.2 Phase 2 (IMPL-P2-003) + F-005 Phase 5 (IMPL-P5-004).
 *
 * Field group per l'immobile (step 2 del wizard).
 * Due blocchi:
 *  1. Indirizzo: via, civico, cap, comune, provincia
 *  2. Dati catastali: foglio, particella, subalterno, rendita, categoria
 *     con <details> tooltip quando showTooltips=true
 *
 * F-005: ring amber + sr-only hint sui campi contestati. Path mappati da
 * `BLOCKED_REASON_FIELDS` per `DATI_ERRATI` e `CONTRATTO_INCOMPLETO`:
 * `immobile.via`, `immobile.civico`, `immobile.comune`, `catastali.foglio`,
 * `catastali.particella`, `catastali.subalterno`.
 *
 * NOTA: contractFullBaseSchema definisce `immobile: indirizzoSchema` direttamente
 *       (NON `immobile: z.object({ indirizzo: indirizzoSchema })`).
 *       I path RHF sono quindi `immobile.via`, `immobile.civico`, ecc.
 *       Riferimento: packages/shared/src/schemas/contract.ts riga 174.
 */
import { Controller, useFormContext } from 'react-hook-form';
import type { ContractDraft } from '@app/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useErrorFields } from '../../hooks/useErrorFields';
import {
  CONTESTED_HINT_TEXT,
  CONTESTED_RING_CLASS,
  contestedHintId,
} from './contestedFieldUtils';

export interface ImmobileFieldsProps {
  showTooltips?: boolean;
}

export function ImmobileFields({ showTooltips = false }: ImmobileFieldsProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<ContractDraft>();
  const imm = errors.immobile;
  const cat = errors.catastali;
  const errorFields = useErrorFields();

  const isViaContested = errorFields.includes('immobile.via');
  const isCivicoContested = errorFields.includes('immobile.civico');
  const isComuneContested = errorFields.includes('immobile.comune');
  const isFoglioContested = errorFields.includes('catastali.foglio');
  const isParticellaContested = errorFields.includes('catastali.particella');
  const isSubalternoContested = errorFields.includes('catastali.subalterno');

  return (
    <div className="space-y-6">
      {/* Indirizzo immobile */}
      <div>
        <p className="text-sm font-medium mb-2">Indirizzo immobile *</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Via */}
          <div className="space-y-1">
            <Label htmlFor="immobile-via">Via / Piazza *</Label>
            <Controller
              name="immobile.via"
              control={control}
              render={({ field }) => (
                <Input
                  id="immobile-via"
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!imm?.via}
                  aria-describedby={
                    imm?.via
                      ? 'immobile-via-error'
                      : isViaContested
                        ? contestedHintId('immobile.via')
                        : undefined
                  }
                  className={cn(isViaContested && CONTESTED_RING_CLASS)}
                />
              )}
            />
            {imm?.via && (
              <p id="immobile-via-error" className="text-xs text-destructive">
                {imm.via.message}
              </p>
            )}
            {isViaContested && (
              <span id={contestedHintId('immobile.via')} className="sr-only">
                {CONTESTED_HINT_TEXT}
              </span>
            )}
          </div>

          {/* Civico */}
          <div className="space-y-1">
            <Label htmlFor="immobile-civico">Civico *</Label>
            <Controller
              name="immobile.civico"
              control={control}
              render={({ field }) => (
                <Input
                  id="immobile-civico"
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!imm?.civico}
                  aria-describedby={
                    imm?.civico
                      ? 'immobile-civico-error'
                      : isCivicoContested
                        ? contestedHintId('immobile.civico')
                        : undefined
                  }
                  className={cn(isCivicoContested && CONTESTED_RING_CLASS)}
                />
              )}
            />
            {imm?.civico && (
              <p id="immobile-civico-error" className="text-xs text-destructive">
                {imm.civico.message}
              </p>
            )}
            {isCivicoContested && (
              <span id={contestedHintId('immobile.civico')} className="sr-only">
                {CONTESTED_HINT_TEXT}
              </span>
            )}
          </div>

          {/* CAP */}
          <div className="space-y-1">
            <Label htmlFor="immobile-cap">CAP *</Label>
            <Controller
              name="immobile.cap"
              control={control}
              render={({ field }) => (
                <Input
                  id="immobile-cap"
                  maxLength={5}
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!imm?.cap}
                  aria-describedby={imm?.cap ? 'immobile-cap-error' : undefined}
                />
              )}
            />
            {imm?.cap && (
              <p id="immobile-cap-error" className="text-xs text-destructive">
                {imm.cap.message}
              </p>
            )}
          </div>

          {/* Comune */}
          <div className="space-y-1">
            <Label htmlFor="immobile-comune">Comune *</Label>
            <Controller
              name="immobile.comune"
              control={control}
              render={({ field }) => (
                <Input
                  id="immobile-comune"
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!imm?.comune}
                  aria-describedby={
                    imm?.comune
                      ? 'immobile-comune-error'
                      : isComuneContested
                        ? contestedHintId('immobile.comune')
                        : undefined
                  }
                  className={cn(isComuneContested && CONTESTED_RING_CLASS)}
                />
              )}
            />
            {imm?.comune && (
              <p id="immobile-comune-error" className="text-xs text-destructive">
                {imm.comune.message}
              </p>
            )}
            {isComuneContested && (
              <span id={contestedHintId('immobile.comune')} className="sr-only">
                {CONTESTED_HINT_TEXT}
              </span>
            )}
          </div>

          {/* Provincia */}
          <div className="space-y-1">
            <Label htmlFor="immobile-provincia">Provincia *</Label>
            <Controller
              name="immobile.provincia"
              control={control}
              render={({ field }) => (
                <Input
                  id="immobile-provincia"
                  maxLength={2}
                  className="uppercase"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  aria-invalid={!!imm?.provincia}
                  aria-describedby={imm?.provincia ? 'immobile-provincia-error' : undefined}
                />
              )}
            />
            {imm?.provincia && (
              <p id="immobile-provincia-error" className="text-xs text-destructive">
                {imm.provincia.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Dati catastali */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <p className="text-sm font-medium">Dati catastali *</p>
          {showTooltips && (
            <details className="relative">
              <summary className="cursor-pointer text-xs text-muted-foreground underline">
                Cosa sono?
              </summary>
              <div className="absolute z-10 mt-1 w-72 rounded-md border bg-background p-3 shadow-md text-xs">
                I dati catastali identificano l&apos;immobile nel catasto terreni o urbano.
                Li trovi nella visura catastale o nell&apos;atto di compravendita.
              </div>
            </details>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* foglio */}
          <div className="space-y-1">
            <Label htmlFor="catastali-foglio">Foglio *</Label>
            <Controller
              name="catastali.foglio"
              control={control}
              render={({ field }) => (
                <Input
                  id="catastali-foglio"
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!cat?.foglio}
                  aria-describedby={
                    cat?.foglio
                      ? 'catastali-foglio-error'
                      : isFoglioContested
                        ? contestedHintId('catastali.foglio')
                        : undefined
                  }
                  className={cn(isFoglioContested && CONTESTED_RING_CLASS)}
                />
              )}
            />
            {cat?.foglio && (
              <p id="catastali-foglio-error" className="text-xs text-destructive">
                {cat.foglio.message}
              </p>
            )}
            {isFoglioContested && (
              <span id={contestedHintId('catastali.foglio')} className="sr-only">
                {CONTESTED_HINT_TEXT}
              </span>
            )}
          </div>

          {/* particella */}
          <div className="space-y-1">
            <Label htmlFor="catastali-particella">Particella *</Label>
            <Controller
              name="catastali.particella"
              control={control}
              render={({ field }) => (
                <Input
                  id="catastali-particella"
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!cat?.particella}
                  aria-describedby={
                    cat?.particella
                      ? 'catastali-particella-error'
                      : isParticellaContested
                        ? contestedHintId('catastali.particella')
                        : undefined
                  }
                  className={cn(isParticellaContested && CONTESTED_RING_CLASS)}
                />
              )}
            />
            {cat?.particella && (
              <p id="catastali-particella-error" className="text-xs text-destructive">
                {cat.particella.message}
              </p>
            )}
            {isParticellaContested && (
              <span id={contestedHintId('catastali.particella')} className="sr-only">
                {CONTESTED_HINT_TEXT}
              </span>
            )}
          </div>

          {/* subalterno (opzionale) */}
          <div className="space-y-1">
            <Label htmlFor="catastali-subalterno">Subalterno</Label>
            <Controller
              name="catastali.subalterno"
              control={control}
              render={({ field }) => (
                <Input
                  id="catastali-subalterno"
                  {...field}
                  value={field.value ?? ''}
                  aria-describedby={
                    isSubalternoContested
                      ? contestedHintId('catastali.subalterno')
                      : undefined
                  }
                  className={cn(isSubalternoContested && CONTESTED_RING_CLASS)}
                />
              )}
            />
            {isSubalternoContested && (
              <span id={contestedHintId('catastali.subalterno')} className="sr-only">
                {CONTESTED_HINT_TEXT}
              </span>
            )}
          </div>

          {/* rendita */}
          <div className="space-y-1">
            <Label htmlFor="catastali-rendita">Rendita catastale (€) *</Label>
            <Controller
              name="catastali.rendita"
              control={control}
              render={({ field }) => (
                <Input
                  id="catastali-rendita"
                  type="number"
                  step="0.01"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      Number.isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber,
                    )
                  }
                  aria-invalid={!!cat?.rendita}
                  aria-describedby={cat?.rendita ? 'catastali-rendita-error' : undefined}
                />
              )}
            />
            {cat?.rendita && (
              <p id="catastali-rendita-error" className="text-xs text-destructive">
                {cat.rendita.message}
              </p>
            )}
          </div>

          {/* categoria (opzionale) */}
          <div className="space-y-1">
            <Label htmlFor="catastali-categoria">Categoria catastale</Label>
            <Controller
              name="catastali.categoria"
              control={control}
              render={({ field }) => (
                <Input id="catastali-categoria" {...field} value={field.value ?? ''} />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
